import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import Page from '../components/common/Page';
import Api from '../lib/Api';
import urlConstants from '../lib/constants/url.constants';
import useToastNotification from '../lib/hooks/useToastNotification';

type SourceCounts = {
  total: number;
  woocommerce: number;
  legacy: number;
  admin: number;
  app: number;
  other: number;
};

type ProgramReportRow = {
  _id: string;
  title: string;
  pid?: string;
  type?: string;
  genders?: string[] | string;
  counts: SourceCounts;
};

type ProgramAccessReportResponse = {
  generatedAt: string;
  totals: SourceCounts;
  programs: ProgramReportRow[];
};

const emptyCounts: SourceCounts = {
  total: 0,
  woocommerce: 0,
  legacy: 0,
  admin: 0,
  app: 0,
  other: 0,
};

const getErrorMessage = (error: any, fallback: string) => {
  if (typeof error === 'string') return error;
  return error?.message || fallback;
};

const formatGenders = (genders?: string[] | string) => {
  if (Array.isArray(genders)) return genders.length ? genders.join(', ') : '-';
  return genders || '-';
};

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' p={3}>
    <Text fontSize='xs' color='gray.500' textTransform='uppercase'>
      {label}
    </Text>
    <Text fontSize='xl' fontWeight='semibold'>
      {value}
    </Text>
  </Box>
);

const ProgramAccessReport = () => {
  const [report, setReport] = useState<ProgramAccessReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const showToast = useToastNotification();

  const loadReport = async () => {
    setIsLoading(true);
    const response = await Api.get<ProgramAccessReportResponse>(
      urlConstants.users.programAccessReport
    );

    if (response.error) {
      showToast({
        title: 'Unable to load report',
        description: getErrorMessage(response.error, 'Please refresh and try again'),
        status: 'error',
      });
    } else {
      setReport(response.data);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    loadReport();
  }, []);

  const filteredPrograms = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (report?.programs || []).filter((program) => {
      const matchesSearch =
        !query ||
        program.title.toLowerCase().includes(query) ||
        String(program.pid || '').toLowerCase().includes(query);

      if (!matchesSearch) return false;
      if (sourceFilter === 'all') return true;
      return (program.counts as any)[sourceFilter] > 0;
    });
  }, [report, search, sourceFilter]);

  const totals = report?.totals || emptyCounts;

  return (
    <Page>
      <VStack align='stretch' spacing={5}>
        <Flex justify='space-between' align='center' gap={3} flexWrap='wrap'>
          <Box>
            <Heading size='lg'>Program Access Report</Heading>
            <Text color='gray.600' mt={1}>
              See how many users have each program.
            </Text>
          </Box>
          <Button
            leftIcon={<RepeatIcon />}
            variant='outline'
            onClick={loadReport}
            isLoading={isLoading}
          >
            Refresh
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 2, md: 6 }} spacing={3}>
          <Stat label='Total access' value={totals.total} />
          <Stat label='Woo paid' value={totals.woocommerce} />
          <Stat label='Legacy' value={totals.legacy} />
          <Stat label='Admin' value={totals.admin} />
          <Stat label='App' value={totals.app} />
          <Stat label='Other' value={totals.other} />
        </SimpleGrid>

        <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' p={4}>
          <Flex justify='space-between' gap={3} flexWrap='wrap' mb={4}>
            <HStack flexWrap='wrap'>
              <Input
                placeholder='Search program'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                maxW='320px'
              />
              <Select
                value={sourceFilter}
                onChange={(event) => setSourceFilter(event.target.value)}
                maxW='220px'
              >
                <option value='all'>All sources</option>
                <option value='woocommerce'>Woo paid</option>
                <option value='legacy'>Legacy</option>
                <option value='admin'>Admin</option>
                <option value='app'>App</option>
                <option value='other'>Other</option>
              </Select>
            </HStack>
            {report?.generatedAt && (
              <Text color='gray.500' fontSize='sm'>
                Updated {new Date(report.generatedAt).toLocaleString()}
              </Text>
            )}
          </Flex>

          {isLoading ? (
            <Flex py={12} justify='center'>
              <Spinner />
            </Flex>
          ) : (
            <TableContainer>
              <Table variant='simple'>
                <Thead>
                  <Tr>
                    <Th>Program</Th>
                    <Th>Gender</Th>
                    <Th isNumeric>Total</Th>
                    <Th isNumeric>Woo Paid</Th>
                    <Th isNumeric>Legacy</Th>
                    <Th isNumeric>Admin</Th>
                    <Th isNumeric>App</Th>
                    <Th isNumeric>Other</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredPrograms.map((program) => (
                    <Tr key={program._id}>
                      <Td fontWeight='semibold'>{program.title}</Td>
                      <Td>{formatGenders(program.genders)}</Td>
                      <Td isNumeric>{program.counts.total}</Td>
                      <Td isNumeric>{program.counts.woocommerce}</Td>
                      <Td isNumeric>{program.counts.legacy}</Td>
                      <Td isNumeric>{program.counts.admin}</Td>
                      <Td isNumeric>{program.counts.app}</Td>
                      <Td isNumeric>{program.counts.other}</Td>
                    </Tr>
                  ))}
                  {filteredPrograms.length === 0 && (
                    <Tr>
                      <Td colSpan={8}>
                        <Text textAlign='center' color='gray.500' py={8}>
                          No programs found.
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </VStack>
    </Page>
  );
};

export default ProgramAccessReport;
